import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { Button } from './ui/button';
import { podcastInfo } from '../data/mockData';

const Hero = () => {
  const audioRef = useRef(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    // Suppress media element errors in dev overlay
    const suppressMediaError = (e) => {
      if (e.target instanceof HTMLMediaElement || e.target instanceof HTMLSourceElement) {
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener('error', suppressMediaError, true);

    const handleInteraction = () => {
      if (audioRef.current && !isAudioPlaying) {
        audioRef.current.play().then(() => setIsAudioPlaying(true))
          .catch(err => console.log('Audio autoplay prevented:', err));
      }
    };
    document.addEventListener('click', handleInteraction, { once: true });
    return () => {
      document.removeEventListener('click', handleInteraction);
      window.removeEventListener('error', suppressMediaError, true);
    };
  }, [isAudioPlaying]);

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (isAudioPlaying) { audioRef.current.pause(); setIsAudioPlaying(false); }
    else { audioRef.current.play().then(() => setIsAudioPlaying(true)).catch(() => {}); }
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen bg-black" style={{ overflow: 'clip' }}>
      <audio ref={audioRef} loop
        style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0 }}
        src="https://customer-assets.emergentagent.com/job_reggae-naketa-show/artifacts/gv2pethz_AUD-20260308-WA0018.m4a"
        preload="auto" />

      {/* Tiled Background */}
      <div className="absolute inset-0 z-0 tiled-bg"
        style={{ backgroundImage: `url("https://customer-assets.emergentagent.com/job_reggae-naketa-show/artifacts/ggorv0jl_Background.jpg")` }} />
      <div className="absolute inset-0 z-0 bg-black/45" />

      {/* Audio Controls */}
      <div className="absolute bottom-8 right-8 z-50 flex gap-2" data-testid="audio-controls">
        <Button size="icon" data-testid="audio-play-btn"
          className="bg-purple-800/80 hover:bg-purple-700 text-white shadow-lg border border-purple-500/50"
          onClick={toggleAudio}>
          {isAudioPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </Button>
        <Button size="icon" data-testid="audio-mute-btn"
          className="bg-purple-800/80 hover:bg-purple-700 text-white shadow-lg border border-purple-500/50"
          onClick={toggleMute}>
          {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </Button>
      </div>

      <div className="relative z-10 w-full" style={{ paddingTop: 'clamp(70px, 14vw, 220px)' }}>

        {/* Title + Subtitle block */}
        <div className="w-full">
          <div className="flex items-center hero-title-group" style={{ overflow: 'visible', position: 'relative', left: '50%', transform: 'translateX(-50%)', width: 'fit-content' }}>
            {/* Text column: title text + subtitle share same width */}
            <div className="flex flex-col" style={{ overflow: 'visible' }}>
              {/* Title text row: NO [NAKETA] APOLOG */}
              <div className="flex items-center"
                data-testid="hero-title-row"
                style={{ height: 'clamp(28px, 14.5vw, 180px)', overflow: 'visible' }}>
                <span className="hero-title-text leading-none select-none" data-testid="hero-text-no"
                  style={{ fontSize: 'clamp(28px, 14.5vw, 180px)', letterSpacing: '-2px', position: 'relative', zIndex: 2 }}>
                  NO
                </span>
                <div data-testid="hero-naketa-image" className="relative flex-shrink-0"
                  style={{
                    width: 'clamp(55px, 24vw, 340px)',
                    height: 'clamp(100px, 46vw, 620px)',
                    marginLeft: 'clamp(-100px, -12vw, -15px)',
                    marginRight: 'clamp(-100px, -12vw, -15px)',
                    zIndex: 10,
                  }}>
                  <div className="relative w-full h-full"
                    style={{
                      WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 75%, rgba(0,0,0,0) 100%)',
                      maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 75%, rgba(0,0,0,0) 100%)',
                    }}>
                    <img src="https://customer-assets.emergentagent.com/job_reggae-naketa-show/artifacts/37iaoi6w_keta1%20copy.png"
                      alt="Naketa Jackson" className="w-full h-full object-contain object-bottom" />
                  </div>
                </div>
                <span className="hero-title-text leading-none select-none" data-testid="hero-text-apolog"
                  style={{ fontSize: 'clamp(28px, 14.5vw, 180px)', letterSpacing: '-2px', position: 'relative', zIndex: 2 }}>
                  APOLOG
                </span>
              </div>
              {/* Subtitle — exactly NO to G width */}
              <h2 data-testid="hero-subtitle"
                className="subtitle-metallic hero-subtitle mt-0 relative z-10 w-full"
                style={{ fontSize: 'clamp(8px, 3.2vw, 52px)', marginTop: '-5px', textAlign: 'justify', textAlignLast: 'justify' }}>
                NAKETA JACKSON'S PODCAST
              </h2>
            </div>

            {/* MIC = I — flush against G, no gap */}
            <div data-testid="hero-mic-logo" className="relative flex-shrink-0 mic-drop-anim"
              style={{
                height: 'clamp(50px, 29vw, 360px)',
                width: 'clamp(50px, 29vw, 360px)',
                marginLeft: 'clamp(-145px, -9.5vw, -45px)',
                zIndex: 4,
                alignSelf: 'flex-start',
                border: 'none',
                outline: 'none',
              }}>
              <img src={podcastInfo.branding.logoMic} alt="I"
                className="w-full h-full object-contain mic-pulsate"
                style={{ filter: 'drop-shadow(0 0 12px rgba(168, 85, 247, 0.6))', border: 'none', outline: 'none', mixBlendMode: 'lighten', WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 4%, black 96%, transparent 100%)', WebkitMaskComposite: 'destination-in', maskImage: 'linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 4%, black 96%, transparent 100%)', maskComposite: 'intersect' }} />
            </div>
          </div>
        </div>

        {/* Video — replaced with user's .mov file */}
        {!videoError && (
        <div className="max-w-4xl mx-auto mt-10 mb-10 px-4 relative z-10" data-testid="hero-video">
          <div className="relative">
            <div className="absolute -inset-4 bg-purple-500 blur-3xl opacity-30 rounded-3xl" />
            <div className="relative aspect-video rounded-3xl border-4 border-purple-500 overflow-hidden bg-black">
              <video
                width="100%" height="100%"
                controls
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
                poster=""
                onError={() => setVideoError(true)}
              >
                <source src="https://customer-assets.emergentagent.com/job_6efdf00b-2eb4-4fd8-a5de-0bea7cc4b344/artifacts/7t62oegm_Screen%20Recording%202026-03-09%20at%205.11.45%20AM.mov" type="video/quicktime" />
                <source src="https://customer-assets.emergentagent.com/job_6efdf00b-2eb4-4fd8-a5de-0bea7cc4b344/artifacts/7t62oegm_Screen%20Recording%202026-03-09%20at%205.11.45%20AM.mov" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
        )}

        {/* Description */}
        <p className="max-w-3xl mx-auto text-base md:text-lg text-gray-300 leading-relaxed mb-8 px-4 text-center relative z-10">
          {podcastInfo.description}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 px-4 relative z-10" data-testid="hero-cta-buttons">
          <Button size="lg" data-testid="listen-now-btn"
            className="bg-gradient-to-r from-purple-800 to-purple-700 hover:from-purple-700 hover:to-purple-600 text-white font-bold px-10 py-6 text-lg rounded-xl border border-purple-500/40"
            onClick={() => window.location.href = '#episodes'}>
            <Play className="mr-2 h-5 w-5" fill="currentColor" />
            LISTEN NOW
          </Button>
          <Button size="lg" data-testid="subscribe-btn"
            className="bg-gradient-to-r from-purple-900 to-purple-800 hover:from-purple-800 hover:to-purple-700 text-purple-200 font-bold px-10 py-6 text-lg rounded-xl border border-purple-600/40"
            onClick={() => window.location.href = '#subscribe'}>
            SUBSCRIBE FREE
          </Button>
        </div>

        {/* Scroll Indicator */}
        <div className="mt-12 mb-8 text-center relative z-10">
          <div className="inline-flex flex-col items-center text-purple-400 cursor-pointer"
            onClick={() => document.getElementById('episodes')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="scroll-indicator">
            <div className="h-10 w-10 rounded-full border-2 border-purple-500/60 flex items-center justify-center mb-2">
              <div className="h-2 w-2 bg-purple-400 rounded-full animate-ping" />
            </div>
            <span className="text-xs font-bold tracking-wider" style={{ fontFamily: "'Prata', serif" }}>EXPLORE EPISODES</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
